import type { ReactNode } from "react";
import { Alert, Typography } from "@mui/material";
import type { LaseredCondition } from "@/types";
import { formatDate } from "@/utils/formatters/dateFormatter";
import {
  EXPECTED_TRIGGER_NOT_APPLIED,
  expectedTriggerNotApplied,
  hasLiveExpectedTrigger,
} from "@/utils/helpers/expectedTrigger";
import { UserName } from "@/resources/claimants/components/deductible-review/DeductibleDecisionHistory";

/** A DATE field: the calendar day as stored. */
const day = (d?: string) => (d ? formatDate(d.slice(0, 10)) : "—");
/** A timestamp: the user's local date. */
const stamp = (d?: string) => (d ? formatDate(d) : "");

interface ExpectedTriggerSummaryProps {
  condition: LaseredCondition;
  /** Edit / cancel actions for a live expected trigger (policy page only). */
  actions?: ReactNode;
}

/**
 * A condition's expected trigger (#7361 AC8): what it is, who set it, when and
 * why, and whether it is active, overdue, arrived or cancelled. Shown on the
 * policy page's condition rows and on the claimant page's condition detail.
 * A live one outside the condition's incurred window (edited after it was set)
 * is not applied by the resolver, so it reads "Not applied", never as in force.
 */
export const ExpectedTriggerSummary = ({ condition, actions }: ExpectedTriggerSummaryProps) => {
  const et = condition.expected_trigger;
  if (!et) return null;

  if (hasLiveExpectedTrigger(condition)) {
    // The windows were edited after it was set: the resolver ignores it (and
    // opens no overdue review), though its record still reads active or overdue.
    const notApplied = expectedTriggerNotApplied(condition);
    const overdue = et.state === "overdue" && !notApplied;
    return (
      <Alert
        severity={overdue || notApplied ? "warning" : "info"}
        variant="outlined"
        action={actions}
        sx={{ fontSize: "0.8125rem", "& .MuiAlert-message": { py: 0.5 } }}
        data-testid="expected-trigger"
      >
        {notApplied && (
          <Typography variant="body2" fontWeight={600} data-testid="expected-trigger-not-applied">
            {EXPECTED_TRIGGER_NOT_APPLIED}
          </Typography>
        )}
        {overdue && (
          <Typography variant="body2" fontWeight={600} data-testid="expected-trigger-overdue">
            Past its review-by date ({day(et.review_by)}) with no qualifying claim line. The daily check opens a
            deductible review that holds the stop-loss claims.
          </Typography>
        )}
        <Typography variant="body2">
          {notApplied ? "Expected date of service" : "Expected from"} {day(et.expected_date)} · {et.reason} · review by{" "}
          {day(et.review_by)}
        </Typography>
        {notApplied && (
          <Typography variant="caption" color="text.secondary" component="div">
            The incurred window ({day(condition.incurred_start_date)} – {day(condition.incurred_end_date)}) no longer
            covers its date of service, so it does not trigger the conditional laser.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" component="div">
          Set
          {et.set_by && (
            <>
              {" "}
              by <UserName value={et.set_by} />
            </>
          )}
          {et.set_datetime ? ` on ${stamp(et.set_datetime)}` : ""}
        </Typography>
      </Alert>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary" component="div" data-testid="expected-trigger">
      {et.state === "arrived" ? (
        <>
          Expected trigger from {day(et.expected_date)} ({et.reason}) replaced by the claim line, date of service{" "}
          {day(condition.trigger_date)}.
        </>
      ) : (
        <>
          Expected trigger from {day(et.expected_date)} ({et.reason}) cancelled
          {et.cancelled_by && (
            <>
              {" "}
              by <UserName value={et.cancelled_by} />
            </>
          )}
          {et.cancelled_datetime ? ` on ${stamp(et.cancelled_datetime)}` : ""}
          {et.cancel_reason ? `: ${et.cancel_reason}` : ""}.
        </>
      )}
    </Typography>
  );
};
